import { Button, FormLabel, Typography } from '@material-ui/core'
import AddIcon from '@material-ui/icons/Add'
import DeleteOutlineIcon from '@material-ui/icons/DeleteOutline'
import React, { useMemo, useState } from 'react'

import TimePeriodForm from '../../../components/TimePeriodForm'
import { DateObject, gregorianToJalaali } from '../../../helpers/date'
import { getTranslate } from '../../../localization'
import { DateRangeValue } from '../types'
import useStyles from './styles/DateRangeList.styles'

interface DateRangeRowProps {
  range: DateRangeValue
  onChange: (range: DateRangeValue) => void
  onRemove: () => void
  removeLabel: string
  canRemove: boolean
}

/**
 * One picker, with its own open/done state.
 *
 * That state is per-row rather than lifted: `TimePeriodForm` drives its popover through
 * `isOpen` and a separate done-clicked flag, so a single shared pair would open every row
 * at once the moment one was clicked.
 */
const DateRangeRow: React.FC<DateRangeRowProps> = ({
  range,
  onChange,
  onRemove,
  removeLabel,
  canRemove
}) => {
  const classes = useStyles()
  const [open, setOpen] = useState(false)
  const [doneClicked, setDoneClicked] = useState(false)

  // Derived from the value rather than captured from onChange: a loaded promotion never
  // fires onChange, so a captured object would leave every saved range blank on open.
  const dateObject: DateObject = useMemo(
    () => ({
      gregorianStartDate: range.start || undefined,
      gregorianEndDate: range.end || undefined,
      jalaaliStartDate: range.start
        ? gregorianToJalaali(range.start)
        : undefined,
      jalaaliEndDate: range.end ? gregorianToJalaali(range.end) : undefined
    }),
    [range.start, range.end]
  )

  return (
    <div className={classes.row}>
      <div className={classes.picker}>
        <TimePeriodForm
          isOpen={open}
          setTimePickerSelected={setOpen}
          dateObject={dateObject}
          onChange={(next: DateObject) =>
            // Both halves on every change, including to '' when the picker is cleared.
            // Writing only the populated one leaves a stale counterpart that the operator
            // can no longer see but validation still reads.
            onChange({
              start: next.gregorianStartDate || '',
              end: next.gregorianEndDate || ''
            })
          }
          onDoneClickForCloseDatePicker={doneClicked}
          setOnDoneClickForCloseDatePicker={setDoneClicked}
          enableAllDays
        />
      </div>
      {canRemove && (
        <Button
          className={classes.remove}
          onClick={onRemove}
          startIcon={<DeleteOutlineIcon />}
          aria-label={removeLabel}>
          {getTranslate('Remove')}
        </Button>
      )}
    </div>
  )
}

interface DateRangeListProps {
  /** Stable id root, so the group's accessible name and its error can be referenced. */
  name: string
  label: string
  helperText: string
  addLabel: string
  removeLabel: string
  ranges: DateRangeValue[]
  onChange: (ranges: DateRangeValue[]) => void
  error?: string
  /**
   * When false the list renders no rows at all until the operator adds one, rather than a
   * blank row. Used for the optional windows, where an empty list is a meaningful value
   * ("no restriction") and a blank row would look like an unfilled required field.
   */
  startEmpty?: boolean
}

/**
 * A repeatable list of date ranges.
 *
 * Every layer below this already stored many: `include_ranges` and `booking_window_ranges`
 * are jsonb arrays, the proto field is `repeated`, the GraphQL input is a list, and the
 * evaluator iterates all of them (`dateInRanges` / `instantInRanges`). The form was the
 * only place that collapsed the array — it loaded `includeRanges[0]` and saved a
 * single-element list, so a campaign running over two separate holidays could not be
 * expressed, and any save on one that already had two silently dropped the second.
 *
 * Verified against staging before this was built: a promotion created with three stay
 * ranges and two booking ranges round-trips all five intact.
 */
const DateRangeList: React.FC<DateRangeListProps> = ({
  name,
  label,
  helperText,
  addLabel,
  removeLabel,
  ranges,
  onChange,
  error,
  startEmpty
}) => {
  const classes = useStyles()

  const rows =
    ranges.length > 0 || startEmpty ? ranges : [{ start: '', end: '' }]

  const setAt = (index: number, next: DateRangeValue) =>
    onChange(rows.map((range, position) => (position === index ? next : range)))

  const removeAt = (index: number) =>
    onChange(rows.filter((_, position) => position !== index))

  const labelId = `${name}-label`
  const errorId = `${name}-error`

  return (
    // role="group" + aria-labelledby is what gives this control an accessible name, the same
    // way the single date range it replaces got one. TimePeriodForm renders no label of its
    // own, so without this the heading above the rows is visual-only and a screen reader
    // reaches three unnamed date pickers with no way to tell a stay window from a blackout.
    <div
      className={classes.root}
      role="group"
      aria-labelledby={labelId}
      aria-invalid={Boolean(error) || undefined}
      aria-describedby={error ? errorId : undefined}>
      <FormLabel id={labelId} component="legend" className={classes.label}>
        {getTranslate(label)}
      </FormLabel>
      {rows.map((range, index) => (
        <DateRangeRow
          // Index as key is correct here and unusual enough to say why: a range has no id,
          // and its start date is not one either — two rows are legitimately identical
          // while the operator is filling the second one in. Rows are only ever appended or
          // removed, never reordered, so the index is stable for the lifetime of a row.
          key={index}
          range={range}
          onChange={(next) => setAt(index, next)}
          onRemove={() => removeAt(index)}
          removeLabel={getTranslate(removeLabel)}
          canRemove={rows.length > 1 || Boolean(startEmpty)}
        />
      ))}
      <Button
        className={classes.add}
        onClick={() => onChange([...rows, { start: '', end: '' }])}
        startIcon={<AddIcon />}
        size="small">
        {getTranslate(addLabel)}
      </Button>
      <Typography
        id={error ? errorId : undefined}
        variant="caption"
        className={error ? classes.errorText : classes.helper}
        role={error ? 'alert' : undefined}>
        {error || getTranslate(helperText)}
      </Typography>
    </div>
  )
}

export default DateRangeList
